import { Button, Table, Tag, type TableColumnsType } from 'antd';
import { Route, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { unitPath } from '../../fleet/features/units/units.paths';
import { jobPath } from '../../jobs/jobs.paths';
import type { BoardTrip } from '../dispatch.api';
import { tripPaths } from '../../trips/trips.paths';
import type { BoardTripsState } from '../hooks/use-board-trips';
import { canOfferAbort } from '../policies/abort-visibility.policy';
import { boardTripStateTag } from '../policies/board-trip-status.policy';
import { AbortTripButton } from './AbortTripButton';

interface BoardTripsTableProps {
  state: BoardTripsState;
  canViewFleet: boolean;
  canDispatch: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  /** After a trip is aborted: its job re-enters the queue, so the board reloads both panels. */
  onAborted: () => void;
}

function buildColumns({
  canViewFleet,
  canDispatch,
  onAborted,
}: Pick<BoardTripsTableProps, 'canViewFleet' | 'canDispatch' | 'onAborted'>): TableColumnsType<BoardTrip> {
  const columns: TableColumnsType<BoardTrip> = [
    { title: 'Trip', key: 'trip', render: (_, trip) => <Link to={tripPaths.trip(trip.id)}>{trip.number}</Link> },
    {
      title: 'Job',
      key: 'job',
      render: (_, trip) => (
        <div className="flex flex-col">
          <Link to={jobPath(trip.jobId)}>{trip.jobNumber ?? 'Job'}</Link>
          <span className="text-xs text-muted">{trip.customerName}</span>
        </div>
      ),
    },
    {
      title: 'Truck',
      key: 'truck',
      render: (_, trip) => {
        if (!trip.truckRegistration) return '—';
        return canViewFleet ? <Link to={unitPath(trip.truckRegistration)}>{trip.truckRegistration}</Link> : trip.truckRegistration;
      },
    },
    { title: 'Driver', key: 'driver', render: (_, trip) => trip.driverName ?? '—' },
    { title: 'Route', key: 'route', render: (_, trip) => `${trip.originZoneCode ?? '—'} → ${trip.destinationZoneCode ?? '—'}` },
    {
      title: 'State',
      key: 'state',
      render: (_, trip) => {
        const tag = boardTripStateTag(trip.status);
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
  ];
  if (!canDispatch) return columns;
  return [
    ...columns,
    {
      title: 'Actions',
      key: 'actions',
      render: (_, trip) => (canOfferAbort(trip.status, canDispatch) ? <AbortTripButton tripId={trip.id} variant="link" onAborted={onAborted} /> : null),
    },
  ];
}

/** Trips under way and those dispatched today, newest first, loaded a page at a time. */
export function BoardTripsTable({ state, canViewFleet, canDispatch, onLoadMore, onRetry, onAborted }: BoardTripsTableProps) {
  if (state.status === 'loading') return <TableSkeleton label="Loading trips" rows={4} />;
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load trips" description="Something went wrong loading the trips on the road.">
        <Button onClick={onRetry}>Try again</Button>
      </EmptyState>
    );
  }
  if (state.total === 0)
    return <EmptyState icon={Route} title="No trips on the road" description="Trips show here once they are dispatched, and until they are delivered." />;

  return (
    <div className="flex flex-col items-start gap-3">
      <Table
        className="w-full"
        rowKey="id"
        dataSource={state.trips}
        columns={buildColumns({ canViewFleet, canDispatch, onAborted })}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
      {state.trips.length < state.total && (
        <Button loading={state.isLoadingMore} onClick={onLoadMore}>
          Load more ({state.total - state.trips.length} more)
        </Button>
      )}
    </div>
  );
}
